import { Rate } from "@redotech/fx";
import { Currency } from "@redotech/money/currencies";
import { LoadState } from "@redotech/react-util/load";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  createFxSubscriptionStore,
  FxSubscriptionStore,
  FxWatchRates,
  WatchRateRef,
} from "./fx-subscription-store";

export type { FxWatchRates, WatchRateRef } from "./fx-subscription-store";

export interface FxContextValue {
  /** The store currency amounts are presented in (the main figure). */
  storeCurrency: Currency;
  /** Register a listener for a pair's live rate; returns an unsubscribe fn. */
  subscribe: (
    ref: WatchRateRef,
    listener: (state: LoadState<Rate>) => void,
  ) => () => void;
  /** Pairs with at least one mounted consumer. */
  activeRefs: WatchRateRef[];
}

export const FxContext = createContext<FxContextValue | undefined>(undefined);

export function useFx(): FxContextValue {
  const context = useContext(FxContext);
  if (!context) {
    throw new Error("useFx must be used within an <FxProvider>");
  }
  return context;
}

/**
 * Builds the {@link FxContextValue} backing an {@link FxContext} provider: a
 * single ref-counted, multiplexed {@link FxSubscriptionStore} plus a live
 * `activeRefs` view.
 *
 * `watchRates` must be a STABLE reference — the store is built from it once. If
 * the transport can change (e.g. becomes available after auth), remount this
 * hook's component (key it on transport readiness) to rebuild.
 */
export function useFxContextValue(
  watchRates: FxWatchRates,
  storeCurrency: Currency,
): FxContextValue {
  const storeRef = useRef<FxSubscriptionStore>();
  if (!storeRef.current) {
    storeRef.current = createFxSubscriptionStore(watchRates);
  }
  const store = storeRef.current;

  const [activeRefs, setActiveRefs] = useState<WatchRateRef[]>([]);
  useEffect(
    () => store.onActiveChange(() => setActiveRefs(store.activeRefs())),
    [store],
  );

  return useMemo<FxContextValue>(
    () => ({ storeCurrency, subscribe: store.subscribe, activeRefs }),
    [storeCurrency, store, activeRefs],
  );
}
